import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar, AlertTriangle } from 'lucide-react';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';

// Routes that remain accessible even when workspace is locked
const LOCKOUT_EXEMPT_PREFIXES = [
  '/settings',
  '/help',
  '/superadmin',
  '/login',
  '/verify-email',
  '/pending-approval',
];

export function TrialBanner() {
  const navigate = useNavigate();
  const { reminderTier, reminderMessage, isTrialing } = useSubscriptionStatus();

  if (!isTrialing || reminderTier === 'none' || !reminderMessage) return null;

  const colorByTier: Record<string, string> = {
    d7: 'bg-[#F5F5F5] text-[#525252]',
    d2: 'bg-[#FFFBEB] text-[#D97706]',
    d1: 'bg-[#FEF2F2] text-[#DC2626]',
    d0: 'bg-[#FEF2F2] text-[#DC2626]',
  };

  return (
    <div className={`${colorByTier[reminderTier]} px-6 py-2 text-sm flex items-center justify-center gap-2`}>
      <Calendar className="h-3.5 w-3.5" />
      <span>{reminderMessage}</span>
      <button
        className="underline underline-offset-2 font-medium ml-1 hover:opacity-80"
        onClick={() => navigate('/settings/billing')}
      >
        Activate FlowTokOS Subscription →
      </button>
    </div>
  );
}

export function TrialLockout() {
  const location = useLocation();
  const { isLocked, status } = useSubscriptionStatus();

  // Allow billing, settings, help, superadmin while locked so users can pay
  const exempt = LOCKOUT_EXEMPT_PREFIXES.some(p => location.pathname.startsWith(p));
  if (!isLocked || exempt) return null;

  const headline = status === 'paused'
    ? 'Your workspace is paused'
    : status === 'canceled'
    ? 'Your subscription was canceled'
    : 'Your trial has ended';

  const description = status === 'paused'
    ? 'Contact us to reactivate your workspace. Your data is safe.'
    : 'Activate your FlowTokOS subscription to continue. Your data is safe and restored immediately on activation.';

  return (
    <div className="fixed inset-0 z-50 bg-white flex items-center justify-center px-6">
      <div className="max-w-[440px] text-center">
        <div className="h-16 w-16 rounded-full bg-[#FEF2F2] flex items-center justify-center mx-auto mb-6">
          <AlertTriangle className="h-7 w-7 text-[#DC2626]" />
        </div>
        <h1 className="text-2xl font-bold text-[#111] mb-3">{headline}</h1>
        <p className="text-sm text-[#525252] leading-relaxed mb-8">{description}</p>
        <div className="flex flex-col gap-3">
          <a href="/settings/billing" className="inline-flex items-center justify-center h-10 px-6 bg-[#1E8FE1] text-white rounded-md text-sm font-semibold hover:bg-[#1567B8]">
            Activate FlowTokOS Subscription
          </a>
          <a href="mailto:michael@flowtokos.com" className="text-sm text-[#A3A3A3] hover:text-[#525252] underline">
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
}
